import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { cn } from '../util/designUtil';
import { formatNumber } from '../util/formatters';

const PaymentSuccessScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [creditsGranted, setCreditsGranted] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPaymentAndRefreshCredits = async () => {
      try {
        if (sessionId) {
          const paymentDoc = await getDoc(doc(db, 'payments', sessionId));
          if (paymentDoc.exists()) {
            const data = paymentDoc.data();
            if (data.creditsGranted) {
              setCreditsGranted(data.creditsGranted);
            } else if (data.creditsToGrant) {
              setCreditsGranted(data.creditsToGrant);
            }
          }
        }

        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUser((prev: any) => prev ? { ...prev, ...userData } : prev);
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentAndRefreshCredits();

    const interval = setInterval(checkPaymentAndRefreshCredits, 3000);
    const timeout = setTimeout(() => clearInterval(interval), 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [sessionId, user?.uid, setUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 flex flex-col items-center justify-center p-4">
      <div className={cn('w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200')}>
        <div className="p-5 sm:p-8 text-center space-y-6">
          <div className="flex justify-center">
            <CheckCircle className="text-green-500" size={64} />
          </div>

          <div className="space-y-2">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Payment Successful!</h1>
            <p className="text-gray-500">
              {isLoading
                ? 'Verifying your payment...'
                : creditsGranted
                  ? `${formatNumber(creditsGranted)} credits have been added to your account.`
                  : 'Your credits will be added shortly once the payment is confirmed.'}
            </p>
          </div>

          {user?.credits !== undefined && (
            <div className="bg-indigo-50 rounded-lg p-4">
              <p className="text-sm text-indigo-600 font-medium">Current Balance</p>
              <p className="text-2xl font-bold text-indigo-700">{formatNumber(user.credits)} Credits</p>
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2">
            <button
              onClick={() => navigate('/home')}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 font-medium rounded-md px-4 py-3 transition-colors"
            >
              Go to Home
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => navigate('/buy-credits')}
              className="w-full text-indigo-600 hover:text-indigo-700 font-medium rounded-md px-4 py-2 transition-colors hover:bg-indigo-50"
            >
              Buy More Credits
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessScreen;
